import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { 
  Button, 
  Card,
  Badge,
  ExpenseSummaryCards
} from '../components';
import { useToast } from '../components/ui/Toast';
import { useApiClient } from '../hooks/useApiClient';
// theme not used

// Types
interface ExpenseSummary {
  total_expenses: number;
  total_amount: number;
  pending_approval: number;
  pending_amount: number;
  approved: number;
  approved_amount: number;
  rejected: number;
  rejected_amount: number;
  by_category: Record<string, { count: number; amount: number }>;
  by_status: Record<string, { count: number; amount: number }>;
  monthly_trend: Array<{ month: string; count: number; amount: number }>;
}

interface RecentExpense {
  id: string;
  amount: number;
  currency: string;
  description: string;
  status: string;
  category: {
    name: string;
  };
  created_at: string;
}

const ExpenseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const apiClient = useApiClient();

  // State
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [recentExpenses, setRecentExpenses] = useState<RecentExpense[]>([]);
  const [loading, setLoading] = useState(true);

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Load summary
        const summaryResponse = await apiClient.get<ExpenseSummary>('/expenses/summary/');
        setSummary(summaryResponse);

        // Load recent expenses
        const recentResponse = await apiClient.get<{ items: RecentExpense[] }>(
          '/expenses?page=1&size=5&sort_by=created_at&sort_desc=true'
        );
        setRecentExpenses(recentResponse.items);

      } catch (error) {
        showToast('Failed to load dashboard data', 'error');
        console.error('Error loading dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [apiClient, showToast]);

  // Get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'draft': return 'draft';
      case 'submitted': return 'submitted';
      case 'pending_approval': return 'pending';
      case 'approved': return 'approved';
      case 'rejected': return 'rejected';
      case 'paid': return 'paid';
      case 'cancelled': return 'cancelled';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 -m-4 sm:-m-6 lg:-m-8 mb-6 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Expense Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Overview of your expenses and spending patterns</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/expenses')}
              className="flex items-center gap-2"
            >
              📊
              View All Expenses
            </Button>
            
            <Button
              onClick={() => navigate('/expenses/create')}
              className="flex items-center gap-2"
            >
              +
              New Expense
            </Button>
          </div>
        </div>
      </div>

        {/* Summary Cards */}
        {summary && (
          <ExpenseSummaryCards
            data={{
              totalExpenses: summary.total_expenses,
              pendingApproval: summary.pending_approval,
              approved: summary.approved,
              rejected: summary.rejected,
              totalAmount: summary.total_amount,
              currency: 'USD',
              monthlyTrend: 0,
              categoryBreakdown: []
            }}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expenses by Category */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Expenses by Category
            </h3>
            {summary && Object.keys(summary.by_category).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(summary.by_category)
                  .sort(([,a], [,b]) => b.amount - a.amount)
                  .slice(0, 5)
                  .map(([category, data]) => (
                    <div key={category} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {category}
                          </span>
                          <span className="text-sm text-gray-600">
                            ${data.amount.toFixed(2)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ 
                              width: `${summary.total_amount > 0 ? (data.amount / summary.total_amount) * 100 : 0}%` 
                            }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-500">
                            {data.count} expense{data.count !== 1 ? 's' : ''}
                          </span>
                          <span className="text-xs text-gray-500">
                            {summary.total_amount > 0 ? ((data.amount / summary.total_amount) * 100).toFixed(1) : 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="h-12 w-12 text-gray-400 mx-auto mb-4 flex items-center justify-center text-2xl">📊</div>
                <p className="text-gray-500">No expense data available</p>
              </div>
            )}
          </Card>

          {/* Expenses by Status */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Expenses by Status
            </h3>
            {summary && Object.keys(summary.by_status).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(summary.by_status)
                  .filter(([, data]) => data.count > 0)
                  .sort(([,a], [,b]) => b.count - a.count)
                  .map(([status, data]) => (
                    <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant={getStatusVariant(status)}>
                          {status.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {data.count} expense{data.count !== 1 ? 's' : ''}
                          </p>
                          <p className="text-xs text-gray-500">
                            ${data.amount.toFixed(2)} total
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          ${(data.amount / data.count).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">avg</p>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="h-12 w-12 text-gray-400 mx-auto mb-4 flex items-center justify-center text-2xl">🕒</div>
                <p className="text-gray-500">No status data available</p>
              </div>
            )}
          </Card>
        </div>

        {/* Recent Expenses */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Expenses
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/expenses')}
            >
              View All
            </Button>
          </div>
          
          {recentExpenses.length > 0 ? (
            <div className="space-y-4">
              {recentExpenses.map((expense) => (
                <div 
                  key={expense.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`/expenses/${expense.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <div className="h-5 w-5 text-primary-600 flex items-center justify-center">💰</div>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {expense.description}
                      </p>
                      <p className="text-xs text-gray-500">
                        {expense.category.name} • {new Date(expense.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {expense.currency} {expense.amount.toFixed(2)}
                      </p>
                      <Badge variant={getStatusVariant(expense.status)} size="sm">
                        {expense.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="h-12 w-12 text-gray-400 mx-auto mb-4 flex items-center justify-center text-2xl">💰</div>
              <p className="text-gray-500 mb-4">No expenses yet</p>
              <Button
                onClick={() => navigate('/expenses/create')}
                className="flex items-center gap-2"
              >
                <span className="h-4 w-4 flex items-center justify-center">+</span>
                Create Your First Expense
              </Button>
            </div>
          )}
        </Card>

        {/* Quick Actions */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/expenses/create')}
              className="flex items-center gap-2 justify-center p-4 h-auto"
            >
              <span className="h-5 w-5 flex items-center justify-center">+</span>
              <div className="text-left">
                <p className="font-medium">New Expense</p>
                <p className="text-xs text-gray-500">Create expense</p>
              </div>
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate('/expenses?status=draft')}
              className="flex items-center gap-2 justify-center p-4 h-auto"
            >
              <span className="h-5 w-5 flex items-center justify-center">🕒</span>
              <div className="text-left">
                <p className="font-medium">Draft Expenses</p>
                <p className="text-xs text-gray-500">Complete drafts</p>
              </div>
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate('/expenses?status=pending_approval')}
              className="flex items-center gap-2 justify-center p-4 h-auto"
            >
              <span className="h-5 w-5 text-yellow-500 flex items-center justify-center">⏳</span>
              <div className="text-left">
                <p className="font-medium">Pending Approval</p>
                <p className="text-xs text-gray-500">Track progress</p>
              </div>
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate('/expenses?status=approved')}
              className="flex items-center gap-2 justify-center p-4 h-auto"
            >
              <span className="h-5 w-5 text-green-500 flex items-center justify-center">✅</span>
              <div className="text-left">
                <p className="font-medium">Approved</p>
                <p className="text-xs text-gray-500">View approved</p>
              </div>
            </Button>
          </div>
        </Card>
    </div>
  );
};

export default ExpenseDashboard;
